import axios from 'axios';
import deepmerge from 'deepmerge';

import accessRequest from '@/manager/services/api/access-request';
import { asyncApi, handleError } from '@/shared/services/api';
import attachment from '@/manager/services/api/attachment';
import baseApi from '@/shared/services/bright_finder';
import claim from '@/manager/services/api/claim';
import enrollment from '@/manager/services/api/enrollment';
import form from '@/manager/services/api/form';
import grant from '@/manager/services/api/grant';
import member from '@/manager/services/api/member';
import provider from '@/manager/services/api/provider';
import seat from '@/manager/services/api/seat';

const managerApi = {
  manager: {
    accessRequest,

    ageGroup: {
      baseUrl(id = null) {
        let url = `${window.api_url}/manager/age_groups`;
        if (id) url += `/${id}`;
        return url;
      },

      async index(params = {}, successCallback = null, failureCallback = null) {
        return axios
          .get(this.baseUrl(), { params })
          .then((response) => {
            if (successCallback) successCallback(response);
            return response;
          })
          .catch((error) => {
            if (failureCallback) return failureCallback(error);
            return handleError(error);
          });
      },
    },

    agreement: asyncApi({
      endpoint: '/manager/agreements',
      payloadKey: 'agreement',
    }),

    assessment: {
      baseUrl(id = null) {
        let url = `${window.api_url}/manager/assessments`;
        if (id) url += `/${id}`;
        return url;
      },

      create(resource, success, failure) {
        axios.post(this.baseUrl(), { assessment: resource }).then(success).catch(failure);
      },

      destroy(id, success) {
        axios.delete(this.baseUrl(id)).then(success);
      },

      downloadUrl(id, filename) {
        return `${this.baseUrl(id)}.pdf?filename=${encodeURIComponent(filename)}.pdf`;
      },

      index(filters, success) {
        if (success) {
          return axios.get(this.baseUrl(), { params: filters }).then(success).catch(handleError);
        }

        return axios.get(this.baseUrl(), { params: filters });
      },

      get(id, success) {
        if (success) {
          return axios.get(`${this.baseUrl()}/${id}`).then(success).catch(handleError);
        }

        return axios.get(`${this.baseUrl()}/${id}`);
      },

      update(id, resource, success, failure) {
        if (success) {
          return axios
            .patch(`${this.baseUrl()}/${id}`, { assessment: resource })
            .then(success)
            .catch(failure);
        }

        return axios.patch(`${this.baseUrl()}/${id}`, { assessment: resource });
      },
    },

    attachment,

    billingStatements: asyncApi({
      endpoint: '/manager/billing_statements',
      payloadKey: 'billing_statement',
    }),

    business: asyncApi({
      endpoint: '/manager/businesses',
      payloadKey: 'business',
    }),

    claim,

    courseCompletion: {
      baseUrl(id = null) {
        let url = `${window.api_url}/manager/course_completions`;
        if (id) url += `/${id}`;
        return url;
      },

      create(course_completion) {
        return axios.post(this.baseUrl(), { course_completion });
      },

      downloadUrl(id, filename) {
        const siteId = window.site_id || '';
        return `${this.baseUrl(id)}.pdf?filename=${encodeURIComponent(filename)}.pdf&site_id=${siteId}`;
      },

      get(id, success) {
        axios.get(this.baseUrl(id)).then(success);
      },

      index(params, success) {
        if (success) {
          return axios.get(this.baseUrl(), { params }).then(success);
        }

        return axios.get(this.baseUrl(), { params });
      },
    },

    enrollment,

    form,

    grant,

    invoice: asyncApi({
      endpoint: '/manager/invoices',
      payloadKey: 'invoice',
    }),

    license: asyncApi({
      endpoint: '/manager/licenses',
      payloadKey: 'license',
    }),

    licensingProgram: asyncApi({
      endpoint: '/manager/licensing_programs',
      payloadKey: 'licensing_program',
    }),

    schema: asyncApi({
      endpoint: '/manager/schemas',
      payloadKey: 'schema',
    }),

    invoice_line_item: asyncApi({
      endpoint: '/manager/invoice_line_items',
      payloadKey: 'invoice_line_item',
    }),

    mass_message: {
      baseUrl() {
        return `${window.api_url}/manager/mass_messages`;
      },

      create(massMessage, success, failure) {
        axios.post(this.baseUrl(), { mass_message: massMessage }).then(success).catch(failure);
      },

      index(filters, success) {
        axios.get(this.baseUrl(), { params: filters }).then(success);
      },

      get(id, success) {
        axios.get(`${this.baseUrl()}/${id}`).then(success);
      },
    },

    member,

    message: {
      baseUrl() {
        return `${window.api_url}/manager/messages`;
      },

      create(message, success, failure) {
        axios.post(this.baseUrl(), { message }).then(success).catch(failure);
      },

      get(id, success) {
        axios.get(`${this.baseUrl()}/${id}`).then(success);
      },

      index(filters, success) {
        axios.get(this.baseUrl(), { params: filters }).then(success);
      },

      promiseCreate(message) {
        return axios.post(this.baseUrl(), { message });
      },
    },

    ownershipRequest: {
      ...asyncApi({
        endpoint: '/manager/ownership_requests',
        payloadKey: 'ownership_request',
      }),

      async destroy(id, providerId, successCallback = null, failureCallback = null) {
        return axios
          .delete(this.baseUrl(id) + `?provider_id=${providerId}`)
          .then((response) => {
            if (successCallback) successCallback(response);
            return response;
          })
          .catch((error) => {
            if (failureCallback) return failureCallback(error);
            return handleError(error);
          });
      },
    },

    profile: asyncApi({
      endpoint: '/manager/profile',
      payloadKey: 'profile',
    }),

    provider,

    quiz: {
      baseUrl(id) {
        if (id) {
          return `${window.api_url}/manager/quizzes/${id}`;
        }
        return `${window.api_url}/manager/quizzes`;
      },

      createPayload(value) {
        return { quiz: value };
      },
      async create(createObject, successCallback = null, failureCallback = null) {
        return axios
          .post(this.baseUrl(), this.createPayload(createObject))
          .then((response) => {
            if (successCallback) successCallback(response);
            return response;
          })
          .catch((error) => {
            if (failureCallback) return failureCallback(error);
            return handleError(error);
          });
      },

      get(id, success) {
        axios.get(this.baseUrl(id)).then(success);
      },

      index(params, success) {
        axios.get(this.baseUrl(), { params }).then(success);
      },

      update(id, quiz, success, failure) {
        axios.patch(this.baseUrl(id), { quiz }).then(success).catch(failure);
      },

      promiseUpdate(id, quiz) {
        return axios.patch(this.baseUrl(id), { quiz });
      },
    },

    seat,

    workflowInstance: asyncApi({
      endpoint: '/manager/workflow_instances',
      payloadKey: 'workflow_instance',
    }),

    workflowTask: asyncApi({
      endpoint: '/manager/workflow_tasks',
      payloadKey: 'workflow_task',
    }),

    workflowTaskForm: asyncApi({
      endpoint: '/manager/workflow_tasks',
      pathSuffix: '/form',
      payloadKey: 'form',
    }),

    subsidy: {
      baseUrl(id = null) {
        let url = `${window.api_url}/manager/subsidies`;
        if (id) url += `/${id}`;
        return url;
      },

      attachments(id) {
        return axios.get(`${this.baseUrl(id)}/attachments`).catch(handleError);
      },

      downloadUrl(id, locale, filename) {
        const pdfFilename = filename || `subsidy_${id}_download`;
        return `${this.baseUrl(id)}.pdf?filename=${encodeURIComponent(pdfFilename)}.pdf&locale=${locale}`;
      },
    },

    roomSchedule: {
      baseUrl(id = null) {
        let url = `${window.api_url}/manager/room_schedules`;
        if (id) url += `/${id}`;
        return url;
      },

      async index(params = {}, successCallback = null, failureCallback = null) {
        return axios
          .get(this.baseUrl(), { params })
          .then((response) => {
            if (successCallback) successCallback(response);
            return response;
          })
          .catch((error) => {
            if (failureCallback) return failureCallback(error);
            return handleError(error);
          });
      },
    },

    tourReservation: asyncApi({
      endpoint: '/manager/tour_reservations',
      payloadKey: 'tour_reservation',
    }),

    worksheet: {
      baseUrl() {
        return `${window.api_url}/manager/worksheets`;
      },

      index(filters, success) {
        if (success) {
          return axios.get(this.baseUrl(), { params: filters }).then(success).catch(handleError);
        }

        return axios.get(this.baseUrl(), { params: filters }).then(success);
      },

      get(id, success) {
        if (success) {
          return axios.get(`${this.baseUrl()}/${id}`).then(success).catch(handleError);
        }

        return axios.get(`${this.baseUrl()}/${id}`);
      },

      update(id, assessmentId, resource, success, failure) {
        if (success) {
          return axios
            .patch(`${this.baseUrl()}/${id}?assessment_id=${assessmentId}`, { worksheet: resource })
            .then(success)
            .catch(failure);
        }

        return axios.patch(`${this.baseUrl()}/${id}?assessment_id=${assessmentId}`, {
          worksheet: resource,
        });
      },
    },
  },
};

export default deepmerge.all([baseApi, managerApi]);
